import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, ChevronLeft, ChevronRight } from 'lucide-react';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const programs = [
    {
      title: 'System and Network Engineer',
      tag: 'Beginner',
      tagColor: 'bg-[#16A34A]',
      description: 'Perfect if you are starting from zero and want an in-person classroom bootcamp.',
      seatsFilled: 40,
      link: '/programs/system-network-engineer'
    },
    {
      title: 'DevOps & Cloud Engineer',
      tag: 'DevOps Path',
      tagColor: 'bg-[#2563EB]',
      description: 'Ideal if you want to learn Terraform, Docker, Kubernetes, and CI/CD pipelines.',
      seatsFilled: 30,
      link: '/programs/devops-engineer'
    }
  ];

  // Auto-scroll functionality - 5 seconds
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % programs.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [programs.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % programs.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + programs.length) % programs.length);

  return (
    <section 
      className="relative overflow-hidden py-20"
      style={{ background: 'linear-gradient(135deg, #020617 0%, #0B1120 100%)' }}
    >
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* LEFT COLUMN */}
          <div className="relative z-10">
            <h1 
              className="text-4xl sm:text-5xl lg:text-[56px] leading-[1.1] font-bold text-white mb-6"
              style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}
            >
              The fastest way into IT – even if you're starting from zero.
            </h1>

            <p className="text-lg sm:text-xl text-[#C7D2FE] mb-8 leading-relaxed">
              Choose the right path, learn by doing, and become job ready with real projects, live mentors, and clear career guidance.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <Link
                to="/ai-career-roadmap"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-semibold text-base transition-all duration-300 hover:shadow-[0_20px_50px_rgba(37,99,235,0.4)]"
              >
                Take Free AI Career Assessment
                <ArrowRight size={20} />
              </Link>
              <Link
                to="/programs"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-white/30 hover:border-white/60 hover:bg-white/5 text-white font-semibold text-base transition-all duration-300"
              >
                Explore Job Ready Programs
              </Link>
            </div>

            <p className="text-sm text-[#94A3B8]">
              Built for beginners and career changers – no IT degree required.
            </p>
          </div>

          {/* RIGHT COLUMN - CAREER READINESS ANALYTICS CARD */}
          <div 
            className="relative rounded-[24px] p-8 border border-white/10 backdrop-blur-md"
            style={{ 
              background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
              boxShadow: '0 22px 65px rgba(15, 23, 42, 0.40), inset 0 1px 0 0 rgba(255,255,255,0.1)'
            }}
          >
            {/* TOP SECTION - Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              
              {/* A. TOP LEFT - YOUR CAREER READINESS PANEL */}
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-1" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
                  Your Career Readiness
                </h3>
                <p className="text-xs sm:text-sm text-[#94A3B8] mb-4">Based on your AI career assessment.</p>

                <div className="mb-4">
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-5xl font-bold text-white">78</span>
                    <span className="text-2xl font-semibold text-[#94A3B8]">%</span>
                  </div>
                  <div className="w-full h-4 bg-[#1F2933] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#2563EB] to-[#06B6D4] rounded-full transition-all duration-1000"
                      style={{ width: '78%' }}
                    ></div>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-[#16A34A]/10 border border-[#16A34A]/30">
                  <p className="text-xs font-semibold text-[#94A3B8] mb-1">Recommended Path</p>
                  <p className="text-sm font-bold text-white">IT Job Ready System & Network Engineer</p>
                </div>
              </div>

              {/* B. TOP RIGHT - SLIDER WITH 2 MINI PROGRAM CARDS */}
              <div className="relative">
                <div className="overflow-hidden rounded-xl">
                  <div 
                    className="flex transition-transform duration-500 ease-in-out"
                    style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                  >
                    {programs.map((program, idx) => (
                      <div key={idx} className="w-full flex-shrink-0 px-2">
                        <div className="p-5 rounded-xl bg-white/5 border border-white/10 h-full">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-base font-bold text-white" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
                              {program.title}
                            </h4>
                            <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded-full ${program.tagColor} text-white`}>
                              {program.tag}
                            </span>
                          </div>
                          <p className="text-sm text-[#C7D2FE] mb-4 leading-relaxed">
                            {program.description}
                          </p>
                          <div className="mb-3">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-[#94A3B8]">Seats filled</span>
                              <span className="font-bold text-white">{program.seatsFilled}%</span>
                            </div>
                            <div className="w-full h-2 bg-[#1F2933] rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-[#2563EB] to-[#06B6D4] rounded-full"
                                style={{ width: `${program.seatsFilled}%` }}
                              ></div>
                            </div>
                          </div>
                          <Link 
                            to={program.link}
                            className="text-sm font-semibold text-[#2563EB] hover:text-[#1d4ed8] transition-colors"
                          >
                            View Program →
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Slider Controls */}
                <div className="flex items-center justify-center gap-2 mt-3">
                  <button
                    onClick={prevSlide}
                    className="p-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 transition-all"
                  >
                    <ChevronLeft size={16} className="text-white" />
                  </button>
                  <div className="flex gap-1">
                    {programs.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentSlide(idx)}
                        className={`h-1.5 rounded-full transition-all ${idx === currentSlide ? 'w-6 bg-[#2563EB]' : 'w-1.5 bg-white/30'}`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={nextSlide}
                    className="p-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 transition-all"
                  >
                    <ChevronRight size={16} className="text-white" />
                  </button>
                </div>
              </div>
            </div>

            {/* C. BOTTOM - VIDEO BLOCK */}
            <div className="p-6 rounded-xl bg-white/5 border border-white/10">
              <div className="relative aspect-video rounded-lg overflow-hidden bg-[#1F2933] group">
                <video 
                  id="heroVideo"
                  className="w-full h-full object-cover"
                  loop
                  playsInline
                  poster="https://customer-assets.emergentagent.com/job_program-curriculum/artifacts/5bzlurrn_new_logo.jpg"
                >
                  <source src="https://customer-assets.emergentagent.com/job_techpath-hub/artifacts/i8nvsbjd_8c20247c-1376-4e60-b185-3b618a2b21a3.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <button
                  onClick={(e) => {
                    const video = document.getElementById('heroVideo');
                    const button = e.currentTarget;
                    if (video.paused) {
                      video.play();
                      video.muted = false;
                      button.innerHTML = '<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 24 24" height="28" width="28"><rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect></svg>';
                    } else {
                      video.pause();
                      button.innerHTML = '<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 24 24" height="28" width="28"><path d="M8 5v14l11-7z"></path></svg>';
                    }
                  }}
                  className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-all cursor-pointer"
                >
                  <div className="w-16 h-16 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] flex items-center justify-center transition-all transform hover:scale-110">
                    <Play size={28} className="text-white ml-1" fill="white" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Glow */}
      <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#2563EB]/20 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-[#06B6D4]/20 rounded-full blur-[120px] pointer-events-none"></div>
    </section>
  );
};

export default HeroSection;